import { Injectable, Inject, Renderer } from '@angular/core';
import { Meta } from './../../services/core/angular2-meta';
import { SeoViewModel } from "./../../models/core/seo";
import { DomAdapter, getDOM } from '@angular/platform-browser/src/dom/dom_adapter';
import { DOCUMENT } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';


export interface LinkDefinition {
    charset?: string;
    crossorigin?: string;
    href?: string;
    hreflang?: string;
    media?: string;
    rel?: string;
    rev?: string;
    sizes?: string;
    target?: string;
    type?: string;
    [prop: string]: string;
}

@Injectable()
export class SeoService {
    private _dom: DomAdapter = getDOM();
    constructor(@Inject(DOCUMENT) private _document: any,
        private metaService: Meta,
        private translate: TranslateService,
    ) {
    }

    setSeoInformation(seoInformation: SeoViewModel) {
        this.metaService.updateTag('name=Keywords', { name: 'Keywords', content: seoInformation.Keywords })
        this.metaService.updateTag('name=Description', { name: 'Description', content: seoInformation.Description })
        this.metaService.updateTag('property="og:description"', { property: 'og:description', content: seoInformation.Description })
        this.metaService.updateTag('property="og:title"', { property: 'og:title', content: seoInformation.Title })
        this.metaService.updateTag('name=Robots', { name: 'Robots', content: seoInformation.SeoRobots })
        this.metaService.setTitle(seoInformation.Title);
        this.updateCanonicalTag('link', { rel: 'canonical', href: seoInformation.Url })
    }

    setSeoUserArea(url: string) {
        this.metaService.updateTag('name=Keywords', { name: 'Keywords', content: "" })
        this.metaService.updateTag('name=Description', { name: 'Description', content: "" })
        this.metaService.updateTag('property="og:description"', { property: 'og:description', content: "" })
        this.metaService.updateTag('property="og:title"', { property: 'og:title', content: "" })
        this.metaService.updateTag('name=Robots', { name: 'Robots', content: "index,follow" })
        let title = this.getLabel("user_area");
        this.metaService.setTitle(title);

        this.updateCanonicalTag('link', { rel: 'canonical', href: url })
    }

    getLabel(label) {
        let message;

        this.translate.get(label, this.translate).subscribe((res: string) => {
            message = res;
        });

        return message;
    }

    getCanonicalLink(selector: string): HTMLLinkElement {
        if (!selector) return null;
        return this._dom.query(selector);
    }

    updateCanonicalTag(selector: string, tag: LinkDefinition): HTMLLinkElement {
        const canonical: HTMLLinkElement = this.getCanonicalLink(selector);
        if (!canonical) {
            //create element if it doesn't exist
            return this._addInternalCanonical(tag);
        }
        return this._prepareLinkElement(tag, canonical);
    }

    //-- *********************************** ANGULAR SERVER SIDE SEO SETUP START ***********************************
    generateSeoDomServerSide(document, renderer: Renderer, seoData: SeoViewModel) {
        let rootElement, h1, h2, h3, linkEl, nodeList, description;
        if (!seoData)
            return;

        //-- Creating root element which will be hidden, but available for crawling
        rootElement = renderer.createElement(document.body, 'div');
        renderer.setElementClass(rootElement, 'd-none', true);

        nodeList = [];

        //-- Creating h1 element
        if (seoData.H1) {
            h1 = renderer.createElement(document.body, 'h1');
            renderer.createText(h1, seoData.H1);
            nodeList.push(h1);
        }

        //-- Creating h2 element
        if (seoData.H2) {
            h2 = renderer.createElement(document.body, 'h2');
            renderer.createText(h2, seoData.H2);
            nodeList.push(h2);
        }

        //-- Creating h3 element
        if (seoData.H3) {
            h3 = renderer.createElement(document.body, 'h3');
            renderer.createText(h3, seoData.H3);
            nodeList.push(h3);
        }

        //-- Creating p short description element
        if (seoData.ShortDescription) {
            description = renderer.createElement(document.body, 'p');
            renderer.createText(description, seoData.ShortDescription);
            nodeList.push(description);
        }

        //-- Creating p short description element
        if (seoData.LongDescription) {
            description = renderer.createElement(document.body, 'p');
            renderer.createText(description, seoData.LongDescription);
            nodeList.push(description);
        }
        //-- Appending all elements to the root element as a children
        renderer.projectNodes(rootElement, nodeList);

        //generate sitemap list for all pages
        this.generateSiteMapList(document, seoData.SiteMapList);
    }
    
    generateSiteMapList(document, siteMapHtml) {
        let stringToInject="<ul class='d-none'>";
        //this.appendHtmlString(document.body,"<ul>");
        siteMapHtml.forEach(element => {
            stringToInject+= "<li>"+ element+"</li>"
        });
        stringToInject+="</ul>";
        //this.appendHtmlString(document.body,"</ul>");
        this.appendHtmlString(document.body,stringToInject);
    }

    appendHtmlString(el: any, template: string) {
        let div = this._dom.createElement('');
        this._dom.setInnerHTML(div, template);
        this._dom.insertAllBefore(el.lastChild, this._dom.childNodes(div));
    }

    appendScriptsToDOM(el: any, template: string) {
        let div = this._dom.createElement('div');
        this._dom.setInnerHTML(div, template);

        let listEl = this._dom.childNodesAsList(div);
        listEl.forEach(element => this._dom.insertAfter(el.lastChild, element));
    }

    setSeoMetaData(document, renderer: Renderer, seoData: SeoViewModel) {
        if (!seoData)
            return;

        this.updateMetaDataTitle(renderer, seoData.Title || '');
        //-- Setting canonicals
        this.createMetaTagUniversalAttribute(document, renderer, 'link', 'rel', 'canonical', 'href', seoData.Url || '');

        //-- Setting meta tags and open graph tags
        this.createMetaTagUniversalAttribute(document, renderer, 'meta', 'name', 'Keywords', 'content', seoData.Keywords || '');
        this.createMetaTagUniversalAttribute(document, renderer, 'meta', 'name', 'Robots', 'content', seoData.SeoRobots || '');
        this.createMetaTagUniversalAttribute(document, renderer, 'meta', 'name', 'description', 'content', seoData.Description || '');

        this.createMetaTagUniversalProperty(document, renderer, 'meta', 'property', 'og:description', 'content', seoData.Description || '');
        this.createMetaTagUniversalProperty(document, renderer, 'meta', 'property', 'og:title', 'content', seoData.Title || '');
    }

    //-- Updating of title for seo optimization
    updateMetaDataTitle(renderer: Renderer, value) {
        if (renderer && value)
            renderer.setText(renderer.selectRootElement('title'), value);
    }

    //-- Creating meta attributes for seo
    createMetaTagUniversalAttribute(document, renderer: Renderer, elementType, propertyNameFirst, valueFirst, propertyNameSecond = "", valueSecond = "") {
        let elem = renderer.createElement(document.head, elementType);
        if (propertyNameFirst && valueFirst) {
            renderer.setElementAttribute(elem, propertyNameFirst, valueFirst);
        }

        if (propertyNameSecond && valueSecond)
            renderer.setElementAttribute(elem, propertyNameSecond, valueSecond);
    }

    //-- Creating meta properties for social networks seo
    createMetaTagUniversalProperty(document, renderer: Renderer, elementType, propertyNameFirst, valueFirst, propertyNameSecond = "", valueSecond = "") {
        let elem = renderer.createElement(document.head, elementType);
        if (propertyNameFirst && valueFirst) {
            renderer.setElementProperty(elem, propertyNameFirst, valueFirst);
        }

        if (propertyNameSecond && valueSecond)
            renderer.setElementProperty(elem, propertyNameSecond, valueSecond);
    }

    //-- ************************************ ANGULAR SERVER SIDE SEO SETUP END*************************************

    private _addInternalCanonical(tag: LinkDefinition): HTMLLinkElement {
        const canonical: HTMLLinkElement = this._createLinkElement();
        this._prepareLinkElement(tag, canonical);
        this._appendLinkElement(canonical);
        return canonical;
    }

    private _prepareLinkElement(tag: LinkDefinition, el: HTMLLinkElement): HTMLLinkElement {
        Object.keys(tag).forEach((prop: string) => this._dom.setAttribute(el, prop, tag[prop]));
        return el;
    }

    private _appendLinkElement(meta: HTMLLinkElement): void {
        const head = this._document.head;
        this._dom.appendChild(head, meta);
    }

    private _createLinkElement(): HTMLLinkElement {
        return this._dom.createElement('link') as HTMLLinkElement;
    }
}


// WEBPACK FOOTER //
// ./src/../~/angular2-template-loader!./src/+app/services/core/seo.service.ts